import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Heart, Trash2, Calendar, MapPin, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { getTicketTiersArray, getMinTicketPrice } from '../utils/eventHelpers';

interface SavedEvent {
  id: string;
  eventId: string;
  eventTitle: string;
  eventImage: string;
  eventDate: Timestamp | null;
  eventStatus: string;
  eventCategory: string;
  eventLocation: string;
  savedAt: Timestamp | null;
  organizerId: string;
  ticketTiers?: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
  }>;
}

const SavedEvents: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [savedEvents, setSavedEvents] = useState<SavedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
    if (!user) {
      navigate('/login', { state: { from: '/saved-events' } });
      return;
    }
      await fetchSavedEvents();
    };
    
    fetchData();
  }, [user, navigate]);

  const fetchSavedEvents = async () => {
    if (!user) {
      setError('Please log in to view saved events');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const q = query(
        collection(db, 'saves'), 
        where('userId', '==', user.uid)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setSavedEvents([]);
        return;
      }
      
      const events = querySnapshot.docs
        .map(doc => {
          const data = doc.data();
          // Ensure all required fields are present and properly typed
          return {
            id: doc.id,
            eventId: data.eventId || '',
            eventTitle: data.eventTitle || 'Untitled Event',
            eventImage: data.eventImage || '/placeholder-event.jpg',
            eventDate: data.eventDate || null,
            eventStatus: data.eventStatus || 'unknown',
            eventCategory: data.eventCategory || 'Uncategorized',
            eventLocation: data.eventLocation || 'Location not specified',
            savedAt: data.savedAt || null,
            organizerId: data.organizerId || '',
            ticketTiers: data.ticketTiers || []
          } as SavedEvent;
        })
        .filter(event => event.eventStatus !== 'cancelled');
      
      setSavedEvents(events);
    } catch (error) {
      console.error('Error loading saved events:', error);
      setError('Failed to load saved events. Please try again.');
      toast.error('Failed to load saved events');
    } finally {
      setLoading(false);
    }
  };

  const removeSavedEvent = async (eventId: string, saveId: string) => {
    if (!user) {
      toast.error('Please log in to remove saved events');
      return;
    }

    try {
      await deleteDoc(doc(db, 'saves', saveId));
      setSavedEvents(prev => prev.filter(event => event.id !== saveId));
      toast.success('Event removed from saved items');
    } catch (error) {
      console.error('Error removing event:', error);
      toast.error('Failed to remove event');
    }
  };

  const formatDate = (timestamp: Timestamp | null): string => {
    if (!timestamp) return 'Date not available';
    try {
      return format(timestamp.toDate(), 'PPP');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => fetchSavedEvents()}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Redirect handled in useEffect
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Saved Events</h1>
          <p className="text-gray-600 mt-2">Events you've bookmarked for later</p>
        </div>
      </div>

      {savedEvents.length === 0 ? (
        <div className="text-center py-12">
          <Heart className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">No saved events yet</h2>
          <p className="text-gray-600 mb-6">Start exploring events and save your favorites!</p>
          <Button onClick={() => navigate('/events')}>
            Explore Events
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {savedEvents.map((event) => (
            <Card key={event.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div 
                className="h-48 bg-cover bg-center bg-gray-100" 
                style={{ 
                  backgroundImage: `url(${event.eventImage || '/placeholder-event.jpg'})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-xl font-semibold line-clamp-2">
                    {event.eventTitle}
                  </CardTitle>
                  <Badge variant={event.eventStatus === 'upcoming' ? 'default' : 'secondary'}>
                    {event.eventStatus}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="text-sm">
                      {formatDate(event.eventDate)}
                    </span>
                  </div>
                  {event.eventLocation && (
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span className="text-sm line-clamp-1">{event.eventLocation}</span>
                    </div>
                  )}
                  <div className="flex items-center text-gray-600">
                    <Tag className="w-4 h-4 mr-2" />
                    <span className="text-sm">{event.eventCategory}</span>
                  </div>
                  {event.ticketTiers && event.ticketTiers.length > 0 && (
                    <p className="text-sm font-medium text-primary mt-1">
                      From ${getMinTicketPrice(event)?.toFixed(2) || '0.00'}
                    </p>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  variant="default"
                  onClick={() => navigate(`/events/${event.eventId}`)}
                >
                  View Details
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => removeSavedEvent(event.eventId, event.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedEvents; 